import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type {
  FeatureRequest,
  FeatureRequestSpecComment,
  FeatureRequestStatus,
  FeatureRequestType,
} from '../types';

// ─── List / detail / counts ──────────────────────────────────────────────────

export interface FeatureRequestsQuery {
  page?: number;
  status?: FeatureRequestStatus;
  type?: FeatureRequestType;
  search?: string;
}

export function useFeatureRequests(query: FeatureRequestsQuery = {}) {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.status) params.set('status', query.status);
  if (query.type) params.set('type', query.type);
  if (query.search) params.set('search', query.search);
  const qs = params.toString();

  return useQuery({
    queryKey: ['feature-requests', query],
    queryFn: () => api.get<FeatureRequest[]>(`/feature-requests${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
  });
}

export function useFeatureRequest(id: string | undefined) {
  return useQuery({
    queryKey: ['feature-requests', id],
    queryFn: () => api.get<FeatureRequest>(`/feature-requests/${id}`),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useFeatureRequestStatusCounts() {
  return useQuery({
    queryKey: ['feature-requests', 'status-counts'],
    queryFn: () => api.get<Record<string, number>>('/feature-requests/status-counts'),
    staleTime: 30_000,
  });
}

export function useCreateFeatureRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: FeatureRequestType; initialMessage: string }) =>
      api.post<FeatureRequest>('/feature-requests', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['feature-requests'] });
    },
  });
}

export function useTransitionFeatureRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; action: string; reason?: string }) =>
      api.post<FeatureRequest>(`/feature-requests/${vars.id}/transition`, {
        action: vars.action,
        ...(vars.reason ? { reason: vars.reason } : {}),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['feature-requests'] });
      qc.invalidateQueries({ queryKey: ['feature-requests', vars.id] });
    },
  });
}

export function useDeleteFeatureRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/feature-requests/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['feature-requests'] });
      qc.removeQueries({ queryKey: ['feature-requests', id] });
    },
  });
}

export function useAddSpecComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: string }) =>
      api.post<FeatureRequestSpecComment>(`/feature-requests/${vars.id}/spec-comments`, {
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['feature-requests', vars.id] });
    },
  });
}

// ─── Streaming chat (SSE) ────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  reasoning?: string;
  toolEvents: string[];
  pending: boolean;
  createdAt: string;
}

export type ChatPhase = 'idle' | 'thinking' | 'streaming' | 'tool' | 'error';

interface SseEvent {
  event: string;
  data: Record<string, unknown>;
}

function parseSseBlock(raw: string): SseEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) as Record<string, unknown> };
  } catch {
    return null;
  }
}

let chatMsgSeq = 0;
function nextChatId(): string {
  chatMsgSeq = chatMsgSeq + 1;
  return `chat-${Date.now()}-${chatMsgSeq}`;
}

export function useFeatureRequestChat(requestId: string | undefined) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [phase, setPhase] = useState<ChatPhase>('idle');
  const [toolLabel, setToolLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeAssistantId = useRef<string | null>(null);

  const busy = phase === 'thinking' || phase === 'streaming' || phase === 'tool';

  const patchAssistant = useCallback((patch: (m: ChatMessage) => ChatMessage) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === activeAssistantId.current ? patch(m) : m)),
    );
  }, []);

  /**
   * Open an SSE stream to `url`, wire it to the active assistant bubble and
   * settle the phase when it ends. Shared by `send` and the auto-start.
   */
  const runSse = useCallback(
    async (url: string, controller: AbortController, body: Record<string, unknown> = {}) => {
      try {
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          let message = 'Nu am putut genera răspunsul.';
          try {
            const json = (await res.json()) as { error?: string };
            if (json.error) message = json.error;
          } catch {
            /* keep default */
          }
          setError(message);
          setPhase('error');
          patchAssistant((m) => ({ ...m, pending: false }));
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sep = buffer.indexOf('\n\n');
          while (sep !== -1) {
            const block = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            sep = buffer.indexOf('\n\n');
            const parsed = parseSseBlock(block);
            if (!parsed) continue;

            switch (parsed.event) {
              case 'message_start':
                setPhase('thinking');
                break;
              case 'reasoning':
                setPhase((p) => (p === 'idle' || p === 'error' ? p : 'thinking'));
                patchAssistant((m) => ({
                  ...m,
                  reasoning: `${m.reasoning ?? ''}${String(parsed.data.delta ?? '')}`,
                }));
                break;
              case 'content':
                setPhase('streaming');
                patchAssistant((m) => ({
                  ...m,
                  content: m.content + String(parsed.data.delta ?? ''),
                }));
                break;
              case 'tool': {
                const label = String(parsed.data.label ?? parsed.data.name ?? 'Uneltă…');
                setPhase('tool');
                setToolLabel(label);
                patchAssistant((m) => ({ ...m, toolEvents: [...m.toolEvents, label] }));
                break;
              }
              case 'error':
                setError(String(parsed.data.message ?? 'Eroare la generarea răspunsului.'));
                setPhase('error');
                patchAssistant((m) => ({ ...m, pending: false }));
                break;
              case 'done':
                setPhase('idle');
                setToolLabel(null);
                patchAssistant((m) => ({ ...m, pending: false }));
                break;
              case 'end':
                setPhase('idle');
                setToolLabel(null);
                patchAssistant((m) => ({ ...m, pending: false }));
                break;
              default:
                break;
            }
          }
        }

        // Stream finished without an explicit done/error — settle.
        setPhase((p) => (p === 'idle' || p === 'error' ? p : 'idle'));
        setToolLabel(null);
        patchAssistant((m) => ({ ...m, pending: false }));
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Eroare de conexiune.');
        setPhase('error');
        patchAssistant((m) => ({ ...m, pending: false }));
      } finally {
        abortRef.current = null;
      }
    },
    [patchAssistant],
  );

  // Load persisted history when the request changes. If the conversation has
  // no assistant reply yet (fresh request), let the agent open it on its own.
  useEffect(() => {
    if (!requestId) return;
    let cancelled = false;
    setMessages([]);
    setPhase('idle');
    setError(null);
    api
      .get<FeatureRequest>(`/feature-requests/${requestId}`)
      .then(({ data }) => {
        if (cancelled) return;
        const history: ChatMessage[] = (data.messages ?? []).map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.body,
          toolEvents: [],
          pending: false,
          createdAt: m.createdAt,
        }));
        setMessages(history);

        const hasAssistant = history.some((m) => m.role === 'assistant');
        if (!hasAssistant && data.status === 'IN_DISCUSSION') {
          // Auto-start: the agent greets and asks the first questions.
          const assistantId = nextChatId();
          activeAssistantId.current = assistantId;
          setMessages((prev) => [
            ...prev,
            {
              id: assistantId,
              role: 'assistant',
              content: '',
              reasoning: '',
              toolEvents: [],
              pending: true,
              createdAt: new Date().toISOString(),
            },
          ]);
          setPhase('thinking');
          const controller = new AbortController();
          abortRef.current = controller;
          void runSse(`/api/feature-requests/${requestId}/start`, controller);
        }
      })
      .catch(() => {
        /* handled by UI error state */
      });
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [requestId, runSse]);

  const send = useCallback(
    async (text: string) => {
      if (!requestId || busy) return;
      const trimmed = text.trim();
      if (!trimmed) return;

      setError(null);
      const userMsg: ChatMessage = {
        id: nextChatId(),
        role: 'user',
        content: trimmed,
        toolEvents: [],
        pending: false,
        createdAt: new Date().toISOString(),
      };
      const assistantId = nextChatId();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        reasoning: '',
        toolEvents: [],
        pending: true,
        createdAt: new Date().toISOString(),
      };
      activeAssistantId.current = assistantId;
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setPhase('thinking');
      setToolLabel(null);

      const controller = new AbortController();
      abortRef.current = controller;
      await runSse(`/api/feature-requests/${requestId}/messages`, controller, { message: trimmed });
    },
    [requestId, busy, runSse],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { messages, phase, toolLabel, error, busy, send, stop };
}
